import { useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';
import { isAfter } from 'date-fns';
import { isEqual } from 'lodash-es';

import { useFeature } from '@asteria/component-tools/featureflag';

import * as AccountStore from '@asteria/datalayer/stores/accounts';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { useRequestLoader } from '@asteria/utils-hooks/useDataLoader';

export function useHeaderData({ onSubmit }) {
	const hasOverdueFeature = useFeature('forecaster-status-overdue');

	const dataLoader = useRequestLoader({ onSubmit });

	const sourceScenarioData = useSelector(
		ScenarioStore.selectors.sourceScenarioData,
		isEqual,
	);

	const currentBalance = useSelector(AccountStore.selectors.currentBalance);

	const query = useQuery({
		queryKey: ['forecaster', 'header', sourceScenarioData?.name],
		queryFn: async () => {
			const status = ['UNPAID', 'SIGNED'];

			if (hasOverdueFeature) {
				status.push('OVERDUE');
			}

			return await onSubmit?.('forecast:fetch:current', {
				scenarioName: sourceScenarioData?.name,
				dataLoader: dataLoader,
				fields: `
					nodes {
						date
						types {
							type
							total
							probability
							currency
						}
					}
				`,
				status,
			});
		},
		refetchOnMount: true,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		placeholderData: null,
	});

	const currentData = query?.data?.entries ?? [];

	const data = currentData?.reduce?.(
		(acc, entry) => {
			const { date, types } = entry;

			for (const type of types) {
				if (type.type === 'DEPOSIT' && type?.total) {
					acc.total += type?.total;
				}

				if (type.type === 'WITHDRAW' && type?.total) {
					acc.total -= type?.total;
				}
			}

			if (isAfter(new Date(date), new Date(acc.endDate))) {
				acc.endDate = date;
			}

			return acc;
		},
		{ total: currentBalance, endDate: new Date() },
	);

	return {
		translationData: {
			total: data?.total ?? 0,
			endDate: data?.endDate ?? new Date(),
		},
		query: query,
	};
}
